import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { validateEmail } from '../../utils/validators';
import { friendlyAuthError } from '../../utils/authErrors';

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all';

export default function ForgotPassword() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const err = validateEmail(email);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (e2) {
      setError(friendlyAuthError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
          <MailCheck className="h-7 w-7 text-green-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Check your inbox</h2>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
            If an account exists for <span className="font-medium text-neutral-700">{email}</span>, we've
            sent a password reset link.
          </p>
        </div>
        <Link to="/auth/login" className="inline-flex items-center text-sm font-medium text-neutral-900 dark:text-neutral-50 hover:underline">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Forgot password?</h2>
        <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
          Enter your email and we'll send you a link to reset your password.
        </p>
      </div>

      <form className="space-y-6" onSubmit={submit}>
        <div className="space-y-2">
          <label className="text-sm font-medium text-neutral-700">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            autoComplete="email"
            className={inputClass}
          />
        </div>

        {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <p className="text-center">
        <Link to="/auth/login" className="inline-flex items-center text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to sign in
        </Link>
      </p>
    </div>
  );
}
